import type {
  Annotation,
  AdminAnalytics,
  AdminWriter,
  ApiError,
  ApiKey,
  Comment,
  CommentStatus,
  CommentsMode,
  CommentThread,
  DomainInfo,
  FeedSort,
  Invite,
  ModeratedComment,
  Paginated,
  Post,
  PostSummary,
  LiteraryForm,
  PublicAuthor,
  ReadingProgress,
  SiteContext,
  Template,
  TemplateConfig,
  UserRole,
  UserStatus,
  Viewer,
  WriterAnalytics,
} from '@shared/types';
import { lang, t } from '@/lib/i18n';

/**
 * Typed fetch client for the Hono API.
 *
 * The SPA is served from the same origin as the Worker on every hostname —
 * kalampress.com, ram.kalampress.com and poetrybyram.com all hit the same Worker — so
 * requests stay relative and the session cookie travels without any CORS setup.
 */

const BASE = '/api';

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }

  /** First message for a given form field, for inline validation display. */
  issue(field: string): string | undefined {
    return this.issues?.[field]?.[0];
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const isFormData = options.body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: options.signal,
      /*
       * The interface language, not the browser's.
       *
       * The Worker answers errors in whichever half of its `नेपाली / English`
       * message this asks for. Sending navigator.language instead would give a
       * Nepali interface English errors on almost every device in Nepal.
       */
      headers: {
        'Accept-Language': lang(),
        ...(isFormData || options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: isFormData
        ? (options.body as FormData)
        : options.body === undefined
          ? undefined
          : JSON.stringify(options.body),
    });
  } catch (error) {
    // An aborted request is the caller unmounting, not a failure worth showing.
    if ((error as Error).name === 'AbortError') throw error;

    // fetch only rejects when the request never got a response: the dev server
    // is down, or the page is a stale tab pointing at a port nothing serves any
    // more. Say that, instead of letting callers fall back to "something failed".
    throw new ApiClientError(
      `${t('सर्भरसँग सम्पर्क भएन')} — ${window.location.origin}. ` +
        'Is `npm run dev` still running, and is this tab on the port it prints?',
      0,
    );
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const error = (payload ?? {}) as ApiError;
    if (!isJson) {
      // HTML where JSON belongs means the request never reached this API — a
      // missing proxy rule, or another app answering on this port.
      throw new ApiClientError(
        `${t('API ले JSON फर्काएन')} (${response.status}) — ${
          response.headers.get('content-type') ?? 'no content type'
        } instead of JSON — check that /api is proxied to the Worker.`,
        response.status,
      );
    }
    throw new ApiClientError(error.error || `Request failed (${response.status})`, response.status, error.issues);
  }

  return payload as T;
}

export interface FeedFilters {
  category?: string;
  form?: LiteraryForm;
  tag?: string;
  author?: string;
  q?: string;
  sort?: FeedSort;
  page?: number;
  perPage?: number;
}

/** A row of the shared transliteration ranking, as an admin sees it. */
export interface AdminPick {
  roman: string;
  text: string;
  /** Distinct contributors, not total picks. */
  votes: number;
  isBlocked: boolean;
  updatedAt?: string;
}

/** A revision as listed in the history dialog — content fetched separately. */
export interface PostRevisionSummary {
  id: string;
  title: string;
  wordCount: number;
  pageCount: number;
  reason: 'edit' | 'publish' | 'restore';
  createdAt: string;
}

export interface PostInput {
  title: string;
  subtitle?: string | null;
  form: LiteraryForm;
  contentHtml?: string;
  contentJson?: unknown;
  /**
   * Sent with the body, never on its own. The server prunes notes against the
   * text in the same request, so a save that carried one without the other would
   * be judged against a body it does not match.
   */
  annotations?: Annotation[];
  authorNoteHtml?: string | null;
  tags?: string[];
  slug?: string;
  commentsEnabled?: boolean;
}

export const api = {
  // -- session ---------------------------------------------------------------
  /**
   * `handle` and `displayName` are optional: the typing tool's signup asks for
   * neither, because both describe a literary site and mean nothing to somebody
   * who wants an API key. The server derives them from the address.
   */
  register(input: {
    email: string;
    password: string;
    handle?: string;
    displayName?: string;
    penName?: string;
  }) {
    return request<{ user: Viewer }>('/auth/register', { method: 'POST', body: input });
  },
  login(input: { email: string; password: string }) {
    return request<{ user: Viewer }>('/auth/login', { method: 'POST', body: input });
  },
  logout() {
    return request<{ ok: true }>('/auth/logout', { method: 'POST' });
  },
  me() {
    return request<{ user: Viewer }>('/auth/me');
  },

  /** Always resolves — the API deliberately does not say whether the address exists. */
  forgotPassword(email: string) {
    return request<{ ok: true }>('/auth/forgot', { method: 'POST', body: { email } });
  },
  /** Signs the reader in on success, so no second trip through the login form. */
  resetPassword(token: string, password: string) {
    return request<{ user: Viewer }>('/auth/reset', { method: 'POST', body: { token, password } });
  },
  sendVerification() {
    return request<{ ok: true; alreadyVerified?: boolean }>('/auth/verify/send', { method: 'POST' });
  },
  verifyEmail(token: string) {
    return request<{ user: Viewer }>('/auth/verify', { method: 'POST', body: { token } });
  },

  // -- public ----------------------------------------------------------------
  context(signal?: AbortSignal) {
    return request<SiteContext>('/public/context', { signal });
  },
  feed(filters: FeedFilters = {}, signal?: AbortSignal) {
    return request<Paginated<PostSummary>>('/public/feed', { query: { ...filters }, signal });
  },
  /** On a tenant host `handle` is omitted and resolved from the Host header. */
  post(slug: string, handle?: string, page?: number, signal?: AbortSignal) {
    const path = handle
      ? `/public/posts/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}`
      : `/public/posts/${encodeURIComponent(slug)}`;
    return request<{ post: Post }>(path, { query: { page }, signal });
  },

  /** Best-effort: the position is already safe in localStorage. */
  saveProgress(postId: string, page: number, percent: number) {
    return request<{ ok: true }>(`/public/posts/${postId}/progress`, {
      method: 'PUT',
      body: { page, percent },
    });
  },
  continueReading(signal?: AbortSignal) {
    return request<{ items: { post: PostSummary; progress: ReadingProgress }[] }>('/public/reading', {
      signal,
    });
  },
  author(handle: string, signal?: AbortSignal) {
    return request<{ author: PublicAuthor }>(`/public/authors/${encodeURIComponent(handle)}`, { signal });
  },
  tags(signal?: AbortSignal) {
    return request<{ tags: { name: string; slug: string; postCount: number }[] }>('/public/tags', { signal });
  },
  toggleLike(postId: string) {
    return request<{ liked: boolean; likeCount: number }>(`/public/posts/${postId}/like`, { method: 'POST' });
  },

  // -- comments --------------------------------------------------------------
  /** Public: anyone may read a thread, only an account may add to it. */
  comments(postId: string, signal?: AbortSignal) {
    return request<CommentThread>(`/comments/posts/${postId}`, { signal });
  },
  addComment(postId: string, input: { body: string; parentId?: string | null }) {
    return request<{ comment: Comment }>(`/comments/posts/${postId}`, { method: 'POST', body: input });
  },
  editComment(id: string, body: string) {
    return request<{ comment: Comment }>(`/comments/${id}`, { method: 'PATCH', body: { body } });
  },
  /** Hide or restore — the post's writer and admins only. */
  setCommentStatus(id: string, status: CommentStatus) {
    return request<{ comment: Comment }>(`/comments/${id}/status`, { method: 'POST', body: { status } });
  },
  deleteComment(id: string) {
    return request<{ ok: true }>(`/comments/${id}`, { method: 'DELETE' });
  },
  /** The writer's own moderation queue: every comment on their posts. */
  moderationQueue(
    filters: { status?: 'all' | 'visible' | 'hidden'; page?: number; perPage?: number } = {},
    signal?: AbortSignal,
  ) {
    return request<Paginated<ModeratedComment>>('/comments/moderation', { query: { ...filters }, signal });
  },

  // -- writer workspace ------------------------------------------------------
  myPosts(
    filters: {
      status?: 'draft' | 'published' | 'all';
      form?: LiteraryForm;
      category?: string;
      q?: string;
      page?: number;
      perPage?: number;
    } = {},
  ) {
    return request<Paginated<PostSummary>>('/posts', { query: { ...filters } });
  },
  getPost(id: string, signal?: AbortSignal) {
    return request<{ post: Post }>(`/posts/${id}`, { signal });
  },
  createPost(input: PostInput) {
    return request<{ post: Post }>('/posts', { method: 'POST', body: input });
  },
  updatePost(id: string, input: Partial<PostInput>) {
    return request<{ post: Post }>(`/posts/${id}`, { method: 'PATCH', body: input });
  },
  publishPost(id: string) {
    return request<{ post: Post }>(`/posts/${id}/publish`, { method: 'POST' });
  },
  /**
   * Hold a piece at the top of the writer's own site.
   *
   * Their site only — the platform feed neither orders by this nor is told about
   * it. Re-pinning something already pinned moves it to the front of the pinned
   * group, which is how pins are reordered.
   */
  pinPost(id: string) {
    return request<{ post: Post }>(`/posts/${id}/pin`, { method: 'POST' });
  },
  unpinPost(id: string) {
    return request<{ post: Post }>(`/posts/${id}/unpin`, { method: 'POST' });
  },
  unpublishPost(id: string) {
    return request<{ post: Post }>(`/posts/${id}/unpublish`, { method: 'POST' });
  },
  deletePost(id: string) {
    return request<{ ok: true }>(`/posts/${id}`, { method: 'DELETE' });
  },
  uploadCover(id: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    return request<{ post: Post }>(`/posts/${id}/cover`, { method: 'POST', body: form });
  },
  removeCover(id: string) {
    return request<{ post: Post }>(`/posts/${id}/cover`, { method: 'DELETE' });
  },
  /** Body image → R2. Returns the /r2 URL to insert into the document. */
  uploadInlineImage(id: string, file: File) {
    const form = new FormData();
    form.append('file', file);
    return request<{ url: string; key: string }>(`/posts/${id}/images`, { method: 'POST', body: form });
  },

  // -- revision history ------------------------------------------------------
  revisions(id: string, signal?: AbortSignal) {
    return request<{ revisions: PostRevisionSummary[] }>(`/posts/${id}/revisions`, { signal });
  },
  revision(id: string, revisionId: string, signal?: AbortSignal) {
    return request<{ revision: PostRevisionSummary & { contentHtml: string } }>(
      `/posts/${id}/revisions/${revisionId}`,
      { signal },
    );
  },
  restoreRevision(id: string, revisionId: string) {
    return request<{ post: Post }>(`/posts/${id}/revisions/${revisionId}/restore`, { method: 'POST' });
  },

  // -- profile & domain ------------------------------------------------------
  updateProfile(input: {
    displayName?: string;
    penName?: string | null;
    bio?: string | null;
    location?: string | null;
    websiteUrl?: string | null;
    commentsMode?: CommentsMode;
    contributesPicks?: boolean;
  }) {
    return request<{ user: Viewer }>('/users/me', { method: 'PATCH', body: input });
  },
  changePassword(input: { currentPassword: string; newPassword: string }) {
    return request<{ ok: true }>('/users/me/password', { method: 'POST', body: input });
  },
  uploadAvatar(file: File) {
    const form = new FormData();
    form.append('file', file);
    return request<{ user: Viewer }>('/users/me/avatar', { method: 'POST', body: form });
  },
  // -- themes ----------------------------------------------------------------
  myTemplates(signal?: AbortSignal) {
    return request<{ templates: Template[] }>('/users/me/templates', { signal });
  },
  /** Picks a theme. Clears any tokens the writer had changed by hand. */
  chooseTemplate(templateKey: string | null) {
    return request<{ user: Viewer }>('/users/me/template', { method: 'PATCH', body: { templateKey } });
  },
  /** The writer's own tweaks on top of that theme; null puts it back as curated. */
  setThemeOverrides(overrides: Partial<TemplateConfig> | null) {
    return request<{ user: Viewer }>('/users/me/theme', { method: 'PATCH', body: { overrides } });
  },

  // -- API keys (the typing tool) --------------------------------------------
  apiKeys(signal?: AbortSignal) {
    return request<{ keys: ApiKey[] }>('/keys', { signal });
  },
  /** The response carries the secret once; it is only ever stored as a hash. */
  createApiKey(name: string) {
    return request<{ key: ApiKey }>('/keys', { method: 'POST', body: { name } });
  },
  revokeApiKey(id: string) {
    return request<{ key: ApiKey }>(`/keys/${id}`, { method: 'DELETE' });
  },

  // -- writer analytics ------------------------------------------------------
  myAnalytics(days = 30, signal?: AbortSignal) {
    return request<WriterAnalytics>('/analytics/overview', { query: { days }, signal });
  },

  // -- invitations (public side) ---------------------------------------------
  invite(code: string, signal?: AbortSignal) {
    return request<{
      invite: { email: string | null; handle: string | null; displayName: string | null; note: string | null; expiresAt: string };
    }>(`/public/invites/${encodeURIComponent(code)}`, { signal });
  },
  join(code: string, input: { email: string; password: string; handle: string; displayName: string; penName?: string }) {
    return request<{ user: Viewer }>(`/auth/join/${encodeURIComponent(code)}`, { method: 'POST', body: input });
  },

  domain(signal?: AbortSignal) {
    return request<{ domain: DomainInfo }>('/users/me/domain', { signal });
  },
  addDomain(hostname: string) {
    return request<{ domain: DomainInfo; user: Viewer }>('/users/me/domain', {
      method: 'POST',
      body: { hostname },
    });
  },
  verifyDomain() {
    return request<{ domain: DomainInfo; user: Viewer }>('/users/me/domain/verify', { method: 'POST' });
  },
  removeDomain() {
    return request<{ domain: DomainInfo; user: Viewer }>('/users/me/domain', { method: 'DELETE' });
  },
};

/**
 * Admin surface. Every call 404s for non-admins — the server does not reveal
 * that these routes exist — so the UI must gate on `viewer.role` as well.
 */
export const adminApi = {
  overview(days = 30, signal?: AbortSignal) {
    return request<AdminAnalytics>('/admin/overview', { query: { days }, signal });
  },

  writers(
    filters: { q?: string; status?: 'all' | 'active' | 'suspended'; page?: number; perPage?: number } = {},
    signal?: AbortSignal,
  ) {
    return request<Paginated<AdminWriter>>('/admin/writers', { query: { ...filters }, signal });
  },
  updateWriter(id: string, input: { role?: UserRole; status?: UserStatus }) {
    return request<{ writer: AdminWriter }>(`/admin/writers/${id}`, { method: 'PATCH', body: input });
  },
  resetWriterPassword(id: string) {
    return request<{ temporaryPassword: string }>(`/admin/writers/${id}/reset-password`, { method: 'POST' });
  },
  deleteWriter(id: string) {
    return request<{ ok: true }>(`/admin/writers/${id}`, { method: 'DELETE' });
  },

  invites(signal?: AbortSignal) {
    return request<{ items: Invite[] }>('/admin/invites', { signal });
  },
  createInvite(input: { email?: string; handle?: string; displayName?: string; note?: string; expiresInDays?: number }) {
    return request<{ invite: Invite; sent: boolean }>('/admin/invites', { method: 'POST', body: input });
  },
  resendInvite(id: string) {
    return request<{ invite: Invite; sent: boolean }>(`/admin/invites/${id}/send`, { method: 'POST' });
  },
  revokeInvite(id: string) {
    return request<{ invite: Invite }>(`/admin/invites/${id}`, { method: 'DELETE' });
  },

  templates(signal?: AbortSignal) {
    return request<{ templates: Template[] }>('/admin/templates', { signal });
  },

  /** The shared transliteration ranking, and the veto over it. */
  picks(filters: { q?: string; page?: number; perPage?: number } = {}, signal?: AbortSignal) {
    return request<Paginated<AdminPick>>('/admin/picks', { query: { ...filters }, signal });
  },
  blockPick(input: { roman: string; text: string; isBlocked: boolean }) {
    return request<{ pick: AdminPick }>('/admin/picks/block', { method: 'POST', body: input });
  },
  createTemplate(input: {
    key: string;
    name: string;
    nameNp: string;
    description?: string;
    config: TemplateConfig;
  }) {
    return request<{ template: Template }>('/admin/templates', { method: 'POST', body: input });
  },
  updateTemplate(
    id: string,
    input: {
      name?: string;
      nameNp?: string;
      description?: string | null;
      config?: Partial<TemplateConfig>;
      isEnabled?: boolean;
      isDefault?: boolean;
    },
  ) {
    return request<{ template: Template }>(`/admin/templates/${id}`, { method: 'PATCH', body: input });
  },
  deleteTemplate(id: string) {
    return request<{ ok: true }>(`/admin/templates/${id}`, { method: 'DELETE' });
  },
};
